import { useEffect, useMemo, useRef, useState } from 'react'
import { useAgentStream } from '../hooks/useAgentStream'
import { AgentAttachment, AgentConversationScope, AgentMessage, ToolCallRecord } from '@/services/agent/service'
import { useAgentConversation, useInvalidateConversations } from '@/services/agent/queries'
import AssistantTurn from './AssistantTurn'
import UserMessage from './UserMessage'
import EmptyState from './EmptyState'
import Composer from './Composer'
import { AssistantSegment } from '../types'
import { useAgentConversationStore, useAgentConversationSlot } from '../store/agentStore'
import { useAgentPageContextStore, getActivePageContext } from '../store/pageContextStore'

type StoredAssistantTurn = {
    role: 'assistant'
    segments: AssistantSegment[]
}

type StoredUserTurn = {
    role: 'user'
    content: string
    attachments?: AgentAttachment[]
}

type StoredTurn = StoredAssistantTurn | StoredUserTurn

const reconstructSegments = (content: string, toolCalls?: ToolCallRecord[]): AssistantSegment[] => {
    const segments: AssistantSegment[] = []

    if (toolCalls && toolCalls.length > 0) {
        for (const tc of toolCalls) {
            if (tc.precedingText) {
                segments.push({ kind: 'text', text: tc.precedingText })
            }
            segments.push({
                kind: 'tool',
                id: tc.id,
                name: tc.name,
                status: 'done',
                args: tc.arguments,
                result: tc.result,
                success: tc.success,
                durationMs: tc.durationMs,
            })
        }
    }

    if (content) {
        segments.push({ kind: 'text', text: content })
    }

    return segments
}

const messagesToTurns = (messages: AgentMessage[]): StoredTurn[] => {
    return messages.map(m => {
        if (m.role === 'user') {
            return { role: 'user', content: m.content, attachments: m.attachments }
        }
        return {
            role: 'assistant',
            segments: reconstructSegments(m.content, m.toolCalls),
        }
    })
}

type Props = {
    /** When true, renders a more compact layout suitable for the slide-in panel. */
    compact?: boolean
    /** Hidden header content slot (e.g. for full-page header). */
    headerSlot?: React.ReactNode
    /** Attachments to seed into a new/draft chat (non-persistent). */
    initialAttachments?: AgentAttachment[]
    /** Optional callback for successful tool results (streaming or committed). */
    onToolResult?: (payload: { name: string; result: unknown }) => void
    /** When true, suppresses the default "How can I help?" empty state surface. */
    hideEmptyState?: boolean
    /** Optional empty state override (used when there is no active conversation). */
    emptyStateSlot?: React.ReactNode
    /** Optional external prefill for the composer input. */
    prefillInput?: string | null
    /** Called after prefill is applied once. */
    onPrefillApplied?: () => void
    /**
     * One-time assistant message injected into the stream UI.
     * Used for “guided workflow” nudges (e.g. after first draft build) so we don’t
     * stuff text into the user’s input box.
     */
    assistantNudge?: { id: string; content: string } | null
    /**
     * Optional controlled attachments list (persistent context).
     * When provided, AgentChat will not clear attachments on submit.
     */
    attachments?: AgentAttachment[]
    onAttachmentsChange?: (next: AgentAttachment[]) => void
    /**
     * Optional collapsible drawer rendered above “Sources” in the composer.
     * Used for “Suggested next edits” and similar guided actions.
     */
    suggestionsSlot?: React.ReactNode
    suggestionsCount?: number
    suggestionsPreview?: string
    /**
     * Optional reset key used by parent pages to force-clear the chat UI.
     * Useful when the user closes a document but we want to discard the current chat surface.
     */
    resetKey?: string | number
    /** Isolates chat state and persisted threads from the main /agent surface. */
    conversationScope?: AgentConversationScope
    /** When in Deliverable Studio, ties new threads to the open draft. */
    studioDraftDocId?: string | null
    /** Keep attachment chips visible during Studio setup. */
    defaultSourcesExpanded?: boolean
}

export default function AgentChat({
    compact = false,
    headerSlot,
    initialAttachments,
    onToolResult,
    hideEmptyState = false,
    emptyStateSlot,
    prefillInput,
    onPrefillApplied,
    assistantNudge,
    attachments: controlledAttachments,
    onAttachmentsChange: onControlledAttachmentsChange,
    suggestionsSlot,
    suggestionsCount,
    suggestionsPreview,
    resetKey,
    conversationScope = 'general',
    studioDraftDocId = null,
    defaultSourcesExpanded = false,
}: Props) {
    const { activeConversationId, isDraftConversation } = useAgentConversationSlot(conversationScope)
    const setActiveConversationId = useAgentConversationStore(s => s.setActiveConversationId)
    void isDraftConversation

    const pageContexts = useAgentPageContextStore(s => s.contexts)
    const activePageContext = useMemo(() => getActivePageContext(pageContexts), [pageContexts])

    const {
        sendMessage,
        isStreaming,
        isAwaitingFirstEvent,
        segments,
        stop,
        error,
        resetStreamState,
        clearSegments,
        clearLastConversationId,
        lastConversationId,
        setOnTitleUpdate,
        lastToolResult,
    } = useAgentStream()

    const [turns, setTurns] = useState<StoredTurn[]>([])
    const [input, setInput] = useState('')
    const [localAttachments, setLocalAttachments] = useState<AgentAttachment[]>([])
    const attachments = controlledAttachments ?? localAttachments
    const setAttachments = onControlledAttachmentsChange ?? setLocalAttachments

    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const committedRef = useRef<boolean>(false)
    const injectedNudgeIdsRef = useRef<Set<string>>(new Set())
    const isNearBottomRef = useRef<boolean>(true)
    const forceAutoScrollRef = useRef<boolean>(false)
    const invalidateConversations = useInvalidateConversations(conversationScope)

    const { data: conversationData, isLoading: isLoadingConversation } =
        useAgentConversation(activeConversationId)

    // Allow parent pages to force-clear the visible chat surface.
    useEffect(() => {
        if (resetKey === undefined) return
        setTurns([])
        setInput('')
        resetStreamState()
        clearSegments()
        committedRef.current = false
        injectedNudgeIdsRef.current = new Set()
        setActiveConversationId(conversationScope, null)
        clearLastConversationId()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey, conversationScope])

    useEffect(() => {
        setOnTitleUpdate(() => invalidateConversations())
        return () => setOnTitleUpdate(null)
    }, [setOnTitleUpdate, invalidateConversations])

    useEffect(() => {
        if (conversationData?.messages) {
            setTurns(messagesToTurns(conversationData.messages))
            resetStreamState()
            committedRef.current = false
        }
    }, [conversationData, resetStreamState])

    // Clear chat surface whenever there's no active conversation — covers both
    // "new chat" (draft mode) and "deselected" (cleared) cases. The data effect
    // above repopulates once a conversation is loaded.
    useEffect(() => {
        if (!activeConversationId) {
            setTurns([])
            resetStreamState()
            committedRef.current = false
        }
    }, [activeConversationId, resetStreamState])

    useEffect(() => {
        if (lastConversationId && lastConversationId !== activeConversationId) {
            setActiveConversationId(conversationScope, lastConversationId)
            invalidateConversations()
            clearLastConversationId()
        }
    }, [
        lastConversationId,
        activeConversationId,
        conversationScope,
        setActiveConversationId,
        invalidateConversations,
        clearLastConversationId,
    ])

    useEffect(() => {
        // Keep scrolling contained to the chat pane (not the whole page).
        // `scrollIntoView` can scroll outer ancestors; for Studio this felt like the header “disappeared”.
        const el = scrollContainerRef.current
        if (!el) return
        if (!isNearBottomRef.current && !forceAutoScrollRef.current) return
        forceAutoScrollRef.current = false
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }, [turns, segments])

    useEffect(() => {
        const el = scrollContainerRef.current
        if (!el) return
        const computeNearBottom = () => {
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
            // Small threshold so it feels natural on mobile (momentum scrolling).
            isNearBottomRef.current = remaining < 120
        }
        computeNearBottom()
        el.addEventListener('scroll', computeNearBottom, { passive: true } as any)
        return () => el.removeEventListener('scroll', computeNearBottom as any)
    }, [])

    // Seed attachments for new/draft chats (e.g. from a deliverable handoff).
    useEffect(() => {
        if (!initialAttachments?.length) return
        if (activeConversationId) return
        if (attachments.length > 0) return
        if (turns.length > 0 || segments.length > 0) return
        setAttachments(initialAttachments)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialAttachments, activeConversationId])

    // Allow parent surfaces to prefill the composer (e.g. "create a new Living Brief for X").
    useEffect(() => {
        const next = String(prefillInput || '').trim()
        if (!next) return
        setInput(next)
        onPrefillApplied?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillInput])

    // Inject a one-time assistant nudge into the visible stream (not persisted).
    useEffect(() => {
        if (!assistantNudge?.id || !assistantNudge?.content) return
        if (injectedNudgeIdsRef.current.has(assistantNudge.id)) return
        injectedNudgeIdsRef.current.add(assistantNudge.id)
        setTurns(prev => [
            ...prev,
            { role: 'assistant', segments: [{ kind: 'text', text: assistantNudge.content }] },
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantNudge?.id])

    const handleSubmit = async () => {
        const trimmed = input.trim()
        if (!trimmed || isStreaming) return

        forceAutoScrollRef.current = true
        setTurns(prev => [...prev, { role: 'user', content: trimmed, attachments: attachments.length ? attachments : undefined }])
        setInput('')
        // If attachments are controlled (persistent), do not clear them on submit.
        if (!controlledAttachments) {
            setAttachments([])
        }
        resetStreamState()
        committedRef.current = false

        await sendMessage(trimmed, activeConversationId, [], {
            pageContext: activePageContext
                ? {
                      label: activePageContext.label,
                      entity: activePageContext.entity,
                      id: activePageContext.id,
                      ref: activePageContext.ref,
                      metadata: activePageContext.metadata,
                  }
                : undefined,
            attachments,
            conversationScope,
            studioDraftDocId: studioDraftDocId || undefined,
        })
    }

    useEffect(() => {
        if (!isStreaming && segments.length > 0 && !committedRef.current) {
            committedRef.current = true
            const finalSegments = segments
            setTurns(prev => [...prev, { role: 'assistant', segments: finalSegments }])
            invalidateConversations()
            clearSegments()
        }
    }, [isStreaming, segments, invalidateConversations, clearSegments])

    useEffect(() => {
        if (!onToolResult) return
        if (!lastToolResult?.success) return
        onToolResult({ name: lastToolResult.name, result: lastToolResult.result })
    }, [lastToolResult, onToolResult])

    const showEmptyState = useMemo(
        () => !activeConversationId && turns.length === 0 && segments.length === 0 && !isStreaming,
        [activeConversationId, turns.length, segments.length, isStreaming]
    )

    const innerMaxWidth = compact ? 'max-w-full' : 'max-w-3xl'
    const containerPadding = compact ? 'px-4 py-5' : 'px-4 py-8'
    const composerPadding = compact ? 'px-3 pt-3 pb-3' : 'px-4 py-4'

    return (
        <div className='flex min-h-0 min-w-0 flex-1 flex-col bg-gradient-to-b from-slate-50/30 via-white to-white'>
            {headerSlot}

            <div ref={scrollContainerRef} className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
                {showEmptyState && !hideEmptyState ? (
                    emptyStateSlot ? (
                        <div className={`mx-auto min-w-0 max-w-full ${innerMaxWidth} ${containerPadding}`}>{emptyStateSlot}</div>
                    ) : (
                        <div className='flex h-full items-center justify-center'>
                            <EmptyState onSelect={prompt => setInput(prompt)} />
                        </div>
                    )
                ) : isLoadingConversation && turns.length === 0 && Boolean(activeConversationId) ? (
                    <div className={`mx-auto ${innerMaxWidth} ${containerPadding} space-y-6`}>
                        {[1, 2, 3].map(i => (
                            <div key={i} className='flex gap-3'>
                                <div className='h-7 w-7 shrink-0 animate-pulse rounded-lg bg-slate-200' />
                                <div className='flex-1 space-y-2'>
                                    <div className='h-4 w-3/4 animate-pulse rounded bg-slate-200' />
                                    <div className='h-4 w-1/2 animate-pulse rounded bg-slate-200' />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={`mx-auto ${innerMaxWidth} ${containerPadding} space-y-6`}>
                        {turns.map((turn, index) =>
                            turn.role === 'user' ? (
                                <UserMessage key={index} content={turn.content} attachments={(turn as any).attachments} />
                            ) : (
                                <AssistantTurn key={index} segments={turn.segments} />
                            )
                        )}

                        {isStreaming && (
                            <AssistantTurn
                                segments={segments}
                                isStreaming={isStreaming}
                                isAwaitingFirstEvent={isAwaitingFirstEvent}
                                showCopy={false}
                            />
                        )}

                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            <div className={`relative border-t border-slate-100 bg-white/95 backdrop-blur ${composerPadding}`}>
                {activePageContext && (
                    <div
                        className={`mx-auto mb-2 ${innerMaxWidth} flex items-center gap-2 rounded-md border border-primary-main/20 bg-primary-main/5 px-2.5 py-1.5 text-xs text-primary-main`}
                        title='This is the page you’re in (helps the agent understand “this/it”). It is not your source attachments.'
                    >
                        <span className='font-semibold'>Page:</span>
                        <span className='truncate'>{activePageContext.label}</span>
                    </div>
                )}
                {error && (
                    <div className={`mx-auto mb-3 ${innerMaxWidth} rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700`}>
                        {error.message}
                    </div>
                )}
                <Composer
                    value={input}
                    onChange={setInput}
                    onSubmit={handleSubmit}
                    onStop={stop}
                    isStreaming={isStreaming}
                    attachments={attachments}
                    onAttachmentsChange={setAttachments}
                    suggestionsSlot={suggestionsSlot}
                    suggestionsCount={suggestionsCount}
                    suggestionsPreview={suggestionsPreview}
                    defaultSourcesExpanded={defaultSourcesExpanded}
                />
            </div>
        </div>
    )
}
